import { useState, useEffect, useCallback } from "react";
import { X, Wrench, Download, Clock, ArrowsClockwise, CurrencyDollar } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { fetchPmSchedule, pmSchedulePdfUrl } from "@/lib/api";
import StarterKitsCard from "@/components/StarterKitsCard";

const SHIFT_OPTS = [
  { value: 1, label: "Single shift · 3,000 h/yr" },
  { value: 2, label: "Two shifts · 6,000 h/yr" },
  { value: 3, label: "Three shifts · 8,000 h/yr" },
];
const YEAR_OPTS = [1, 2, 3, 5, 7, 10];

export default function PmScheduleModal({ productType, displayName = "", brand = "US", customerName = "", quoteId = "", onClose }) {
  const currency = brand === "ACC" ? "CAD" : "USD";
  const sym = currency === "CAD" ? "C$" : "$";
  const [shifts, setShifts] = useState(1);
  const [years, setYears] = useState(10);
  const [includePrices, setIncludePrices] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!productType) return;
    setLoading(true); setError("");
    try {
      const d = await fetchPmSchedule(productType, shifts, years, currency, includePrices);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load PM schedule");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productType, shifts, years, currency, includePrices]);

  useEffect(() => { load(); }, [load]);

  const pdfHref = pmSchedulePdfUrl({
    product_type: productType, shifts, years, brand,
    customer_name: customerName, quote_id: quoteId, include_prices: includePrices,
  });

  const meta = data?.schedule?.meta || {};
  const compPrice = meta.compressor_price || 0;
  const totalPm = meta.grand_total_pm_cost || 0;
  const coverage = meta.pricing_coverage ?? 100;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="pm-schedule-modal">
      <div className="relative w-full max-w-5xl bg-white rounded-sm shadow-xl border border-slate-200 my-8">
        {/* Header — compressor name front and centre so the reader knows
             at a glance what model this PM schedule is for. */}
        <div className={`px-5 py-4 border-b border-slate-200 ${brand === "ACC" ? "bg-red-700" : "bg-blue-700"}`}>
          <div className="flex items-start gap-3">
            <Wrench size={20} weight="bold" className="text-white mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-white/70 text-[10px] font-bold uppercase tracking-[0.15em] mb-0.5">
                Preventive Maintenance Schedule
              </div>
              <div
                data-testid="pm-modal-compressor-name"
                className="text-white font-black text-2xl md:text-3xl leading-tight break-words"
              >
                {displayName || productType || "—"}
              </div>
              <div className="text-white/60 text-[11px] font-mono mt-0.5">
                {productType}
              </div>
            </div>
            <button
              data-testid="pm-modal-close"
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 -mr-1"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Shifts</label>
            <select data-testid="pm-shifts" value={shifts} onChange={(e) => setShifts(Number(e.target.value))}
                    className="rounded-sm border border-slate-300 h-9 text-sm px-2 bg-white">
              {SHIFT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Years</label>
            <select data-testid="pm-years" value={years} onChange={(e) => setYears(Number(e.target.value))}
                    className="rounded-sm border border-slate-300 h-9 text-sm px-2 bg-white">
              {YEAR_OPTS.map((y) => <option key={y} value={y}>{y} year{y === 1 ? "" : "s"}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer mb-1.5 ml-1">
            <input type="checkbox" data-testid="pm-include-prices" checked={includePrices}
                   onChange={(e) => setIncludePrices(e.target.checked)}
                   className="h-4 w-4 accent-emerald-700 rounded-sm" />
            <CurrencyDollar size={14} weight="bold" className={includePrices ? "text-emerald-700" : "text-slate-400"} />
            Show prices
          </label>
          <div className="ml-auto flex gap-2">
            <Button data-testid="pm-refresh" variant="outline" className="h-9 text-xs" onClick={load} disabled={loading}>
              <ArrowsClockwise size={13} weight="bold" className="mr-1" />
              Refresh
            </Button>
            <a data-testid="pm-download-pdf" href={pdfHref} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold">
              <Download size={13} weight="bold" />
              Download PDF
            </a>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          {loading && <div className="text-sm text-slate-500">Loading schedule…</div>}
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{error}</div>}
          {data && !error && (
            <>
              {/* Meta cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                <MetaCard label="Operation" value={meta.shift_label || "—"} />
                <MetaCard label="Total hours" value={meta.total_hours?.toLocaleString() || "—"} />
                {includePrices && (
                  <MetaCard
                    label={`Total PM (${meta.years || 0} yr)`}
                    value={`${sym}${totalPm.toLocaleString(undefined,{maximumFractionDigits:0})}`}
                    accent
                  />
                )}
                {!includePrices && (
                  <MetaCard label="Distinct kits" value={meta.kit_count} />
                )}
              </div>
              {includePrices && coverage < 100 && (
                <div data-testid="pm-coverage-banner" className="mb-3 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-3 py-1.5">
                  Pricing coverage: <b>{coverage}%</b> ({meta.priced_lines}/{meta.total_lines} lines). Some kits aren't yet in the Parts price list; their cost shows as "—".
                </div>
              )}

              {/* Recommended Starter Kits (Break-In + 3,000-hour) with 10% bundle discount */}
              <div className="mb-4">
                <StarterKitsCard productType={productType} brand={brand} shifts={shifts} compressorPrice={compPrice} />
              </div>

              {/* Kit overview */}
              <div className="mb-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 flex items-center gap-1.5">
                  <Clock size={12} weight="bold" /> PM Kit List
                </h3>
                <div className="overflow-x-auto border border-slate-200 rounded-sm">
                  <table className="w-full text-xs" data-testid="pm-kit-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Kit</th>
                        <th className="text-left px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Type (join key)</th>
                        <th className="text-left px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Part</th>
                        <th className="text-center px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Qty</th>
                        <th className="text-right px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Every</th>
                        {includePrices && <th className="text-right px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Unit price</th>}
                        <th className="text-center px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px]">Repeat?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.kit_overview.map((k, i) => (
                        <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50/50"}>
                          <td className="px-2.5 py-1.5 font-mono text-slate-700">{k.kit}</td>
                          <td className="px-2.5 py-1.5 text-slate-800">{k.kit_type}</td>
                          <td className="px-2.5 py-1.5 text-slate-700">
                            {k.part_name ? <>{k.part_name}{k.part_spec ? <span className="text-slate-400"> · {k.part_spec}</span> : null}</> : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-2.5 py-1.5 text-center">{k.qty_per_fire}</td>
                          <td className="px-2.5 py-1.5 text-right font-mono">
                            {k.trigger_hours.toLocaleString()}h{k.trigger_months ? ` · ${k.trigger_months}mo` : ""}
                          </td>
                          {includePrices && (
                            <td className="px-2.5 py-1.5 text-right font-mono">
                              {k.unit_price > 0 ? `${sym}${k.unit_price.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` : <span className="text-slate-300">—</span>}
                            </td>
                          )}
                          <td className="px-2.5 py-1.5 text-center">
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border ${
                              k.repetitive ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-amber-50 border-amber-200 text-amber-800"
                            }`}>
                              {k.repetitive ? "Repeat" : "Once"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Year breakdown */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Year-by-Year Service Actions</h3>
                <div className="space-y-2">
                  {data.schedule.years.map((y) => (
                    <div key={y.year} className="border border-slate-200 rounded-sm" data-testid={`pm-year-${y.year}`}>
                      <div className="flex items-center gap-3 px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-wrap">
                        <span className="text-sm font-bold text-slate-900">Year {y.year}</span>
                        <span className="text-[11px] font-mono text-slate-500">
                          {y.hours_start.toLocaleString()} → {y.hours_end.toLocaleString()} h
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm">
                          {y.total_fires} action{y.total_fires === 1 ? "" : "s"}
                        </span>
                        {includePrices && (
                          <div className="ml-auto flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-emerald-700 font-mono">
                              {sym}{y.year_cost.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </span>
                          </div>
                        )}
                      </div>
                      {y.items.length === 0 ? (
                        <div className="px-3 py-2 text-xs italic text-slate-400">No scheduled actions this year</div>
                      ) : (
                        <ul className="divide-y divide-slate-100 text-xs">
                          {y.items.map((it, idx) => (
                            <li key={idx} className="px-3 py-1.5 flex items-baseline gap-2 flex-wrap">
                              <span className="font-mono text-slate-500 text-[10px] uppercase">{it.kit}</span>
                              <span className="font-semibold text-slate-800">{it.kit_type}</span>
                              {it.part_name && <span className="text-slate-400 text-[11px]">({it.part_name})</span>}
                              <span className="text-slate-500">
                                qty {it.qty_per_fire} × {it.fires} = <b>{it.total_qty}</b>
                              </span>
                              {includePrices && (
                                <span className="ml-auto font-mono text-[11px]">
                                  {it.unit_price > 0 ? (
                                    <>
                                      <span className="text-slate-400">{sym}{it.unit_price.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} × {it.total_qty} = </span>
                                      <b className="text-slate-800">{sym}{it.line_cost.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</b>
                                    </>
                                  ) : (
                                    <span className="text-slate-300">no price</span>
                                  )}
                                </span>
                              )}
                              {!includePrices && (
                                <span className="ml-auto text-[10px] text-slate-400 font-mono">
                                  every {it.trigger_hours.toLocaleString()}h {it.repetitive ? "" : "· one-time"}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 italic">
          {includePrices
            ? <>List prices subject to change. Hide pricing for a clean customer handout via the toggle above or the PDF's <code>include_prices=false</code> option.</>
            : <>Pricing hidden — toggle "Show prices" above or use the PDF with <code>include_prices=false</code> for a clean customer handout.</>}
        </div>
      </div>
    </div>
  );
}

function MetaCard({ label, value, sub, accent }) {
  return (
    <div className={`border rounded-sm px-2.5 py-1.5 ${accent ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm font-bold truncate ${accent ? "text-emerald-800" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
